import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Plus, Search, Users, DollarSign, Gift, Edit, Trash2, Loader2, Globe, Link2, ExternalLink } from 'lucide-react';
import { FinancePageLayout } from '@/components/finance';
import { useEmployees, useEmployeeSummary, useBirthdaysThisMonth, useCreateEmployee, useUpdateEmployee, useDeleteEmployee, useLinkedAppUserIds, type Employee } from '@/hooks/finance/useEmployees';
import { useActiveAppUsersForSelect } from '@/hooks/useAppUsers';
import { useDepartments } from '@/hooks/useDepartments';
import { useSystemRoles } from '@/hooks/useSystemRoles';
import { useLatestExchangeRate } from '@/hooks/finance/useExchangeRates';
import { toast } from 'sonner';
import { createPageUrl } from '@/utils';

// DRE Categories mapping
const DRE_CATEGORIES = [
  { code: '6100', label: 'CSP - Legal Team', group: 'CSP' },
  { code: '6300', label: 'CSP - Customer Success', group: 'CSP' },
  { code: '5200', label: 'CAC - Sales Team', group: 'CAC' },
  { code: '5100', label: 'CAC - Marketing', group: 'CAC' },
  { code: '7100', label: 'G&A - Management', group: 'G&A' },
  { code: '7500', label: 'G&A - Other', group: 'G&A' },
] as const;

const getDreCategoryLabel = (accountCode: string | null) => {
  if (!accountCode) return null;
  return DRE_CATEGORIES.find(c => c.code === accountCode);
};

const employeeSchema = z.object({
  full_name: z.string().min(1, 'Nome é obrigatório').max(100),
  role: z.string().min(1, 'Cargo é obrigatório'),
  department: z.string().optional(),
  business_unit: z.enum(['us', 'br', 'shared']).nullable().optional(),
  base_salary: z.coerce.number().min(0, 'Salário deve ser positivo'),
  salary_currency: z.string().min(1, 'Moeda é obrigatória'),
  hire_date: z.string().optional(),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  cost_type: z.enum(['fixed', 'variable']).optional(),
  account_code: z.string().optional(),
  app_user_id: z.string().optional(),
  // Salary frequency fields
  salary_frequency: z.enum(['monthly', 'biweekly', 'weekly']).optional(),
  weekly_payment_type: z.enum(['fixed', 'hourly']).optional(),
  hourly_rate: z.coerce.number().min(0).optional(),
  projected_hours_per_week: z.coerce.number().min(0).max(168).optional(),
  // Payment schedule fields
  payment_schedule_type: z.enum(['fixed_day', 'weekday', 'biweekly_days']).optional(),
  payment_day_of_month: z.coerce.number().min(1).max(31).optional(),
  payment_day_of_week: z.coerce.number().min(0).max(6).optional(),
  payment_biweekly_day1: z.coerce.number().min(1).max(31).optional(),
  payment_biweekly_day2: z.coerce.number().min(1).max(31).optional(),
});

type EmployeeFormData = z.infer<typeof employeeSchema>;

const formatCurrency = (value: number, currency: string = 'USD') => {
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
  }).format(value);
};

type BusinessUnitFilter = 'all' | 'us' | 'br' | 'shared';

export default function Employees() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [businessUnitFilter, setBusinessUnitFilter] = useState<BusinessUnitFilter>('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [deletingEmployee, setDeletingEmployee] = useState<Employee | null>(null);
  
  const { data: employees, isLoading } = useEmployees();
  const { data: summary } = useEmployeeSummary();
  const { data: birthdays } = useBirthdaysThisMonth();
  const { data: linkedAppUserIds = [] } = useLinkedAppUserIds();
  const { users: appUsers, loading: loadingAppUsers } = useActiveAppUsersForSelect();
  const { departments } = useDepartments();
  const { roles } = useSystemRoles();
  const { data: brlToUsdRate } = useLatestExchangeRate('BRL', 'USD');
  
  const createEmployee = useCreateEmployee();
  const updateEmployee = useUpdateEmployee();
  const deleteEmployee = useDeleteEmployee();

  const form = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      full_name: '',
      role: '',
      department: '',
      business_unit: undefined,
      base_salary: 0,
      salary_currency: 'USD',
      hire_date: '',
      email: '',
      phone: '',
      cost_type: 'fixed',
      account_code: 'auto',
      app_user_id: '',
      // Salary frequency defaults
      salary_frequency: 'monthly',
      weekly_payment_type: undefined,
      hourly_rate: undefined,
      projected_hours_per_week: undefined,
      // Payment schedule defaults
      payment_schedule_type: 'fixed_day',
      payment_day_of_month: undefined,
      payment_day_of_week: undefined,
      payment_biweekly_day1: undefined,
      payment_biweekly_day2: undefined,
    },
  });

  // Watch salary frequency to show conditional fields
  const salaryFrequency = form.watch('salary_frequency');
  const weeklyPaymentType = form.watch('weekly_payment_type');
  const paymentScheduleType = form.watch('payment_schedule_type');

  // Filter app_users that are not already linked to other employees
  const availableAppUsers = appUsers.filter(u => {
    // Always include the app_user linked to the current employee being edited
    if (editingEmployee?.app_user_id === u.id) return true;
    // Exclude app_users already linked to other employees
    return !linkedAppUserIds.includes(u.id);
  });

  const getAppUserDisplayName = (user: typeof appUsers[0]) => {
    if (user.display_name) return user.display_name;
    const parts = [user.first_name, user.last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : user.email || 'Sem nome';
  };

  const getAppUserById = (id: string | null) => {
    if (!id) return null;
    return appUsers.find(u => u.id === id);
  };

  // Filtrar por business unit primeiro
  const buFilteredEmployees = employees?.filter(emp => {
    if (businessUnitFilter === 'all') return true;
    if (businessUnitFilter === 'shared') return emp.business_unit === null;
    return emp.business_unit === businessUnitFilter;
  });

  // Filtrar por busca textual
  const filteredEmployees = buFilteredEmployees?.filter(emp => 
    emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeEmployees = filteredEmployees?.filter(e => e.status === 'active') || [];

  // Calcular totais filtrados
  const filteredSummary = {
    count: activeEmployees.length,
    totalUSD: activeEmployees
      .filter(e => e.salary_currency === 'USD')
      .reduce((sum, e) => sum + (e.base_salary || 0), 0),
    totalBRL: activeEmployees
      .filter(e => e.salary_currency === 'BRL')
      .reduce((sum, e) => sum + (e.base_salary || 0), 0),
  };

  // Calcular total consolidado em USD
  const totalConsolidatedUSD = filteredSummary.totalUSD + 
    (brlToUsdRate?.rate ? filteredSummary.totalBRL * brlToUsdRate.rate : 0);

  const handleOpenDialog = (employee?: Employee) => {
    if (employee) {
      setEditingEmployee(employee);
      // Migrar "mixed" para "fixed" automaticamente
      const costType = employee.cost_type === 'mixed' ? 'fixed' : (employee.cost_type || 'fixed');
      // Parse biweekly days array
      const biweeklyDays = employee.payment_biweekly_days || [];
      form.reset({
        full_name: employee.full_name,
        role: employee.role || '',
        department: employee.department || '',
        business_unit: employee.business_unit,
        base_salary: employee.base_salary || 0,
        salary_currency: employee.salary_currency || 'USD',
        hire_date: employee.hire_date || '',
        email: employee.email || '',
        phone: employee.phone || '',
        cost_type: costType as 'fixed' | 'variable',
        account_code: employee.account_code || 'auto',
        app_user_id: employee.app_user_id || '',
        // Salary frequency fields
        salary_frequency: employee.salary_frequency || 'monthly',
        weekly_payment_type: employee.weekly_payment_type || undefined,
        hourly_rate: employee.hourly_rate || undefined,
        projected_hours_per_week: employee.projected_hours_per_week || undefined,
        // Payment schedule fields
        payment_schedule_type: employee.payment_schedule_type || 'fixed_day',
        payment_day_of_month: employee.payment_day_of_month || undefined,
        payment_day_of_week: employee.payment_day_of_week || undefined,
        payment_biweekly_day1: biweeklyDays[0] || undefined,
        payment_biweekly_day2: biweeklyDays[1] || undefined,
      });
    } else {
      setEditingEmployee(null);
      form.reset({
        full_name: '',
        role: '',
        department: '',
        business_unit: undefined,
        base_salary: 0,
        salary_currency: 'USD',
        hire_date: '',
        email: '',
        phone: '',
        cost_type: 'fixed',
        account_code: 'auto',
        app_user_id: '',
        salary_frequency: 'monthly',
        weekly_payment_type: undefined,
        hourly_rate: undefined,
        projected_hours_per_week: undefined,
        payment_schedule_type: 'fixed_day',
        payment_day_of_month: undefined,
        payment_day_of_week: undefined,
        payment_biweekly_day1: undefined,
        payment_biweekly_day2: undefined,
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingEmployee(null);
    form.reset();
  };

  const onSubmit = async (data: EmployeeFormData) => {
    try {
      const businessUnitToSave = data.business_unit === 'shared' ? null : (data.business_unit || null);
      const appUserIdToSave = data.app_user_id === '' || data.app_user_id === 'none' ? null : data.app_user_id;
      
      // Build biweekly days array
      const paymentBiweeklyDays = data.payment_schedule_type === 'biweekly_days' && data.payment_biweekly_day1 && data.payment_biweekly_day2
        ? [data.payment_biweekly_day1, data.payment_biweekly_day2]
        : null;
      
      // If linking to an app_user, sync name and email
      let syncedData = { ...data };
      if (appUserIdToSave) {
        const linkedUser = getAppUserById(appUserIdToSave);
        if (linkedUser) {
          syncedData.full_name = getAppUserDisplayName(linkedUser);
          syncedData.email = linkedUser.email || data.email;
        }
      }

      // Prepare frequency-related fields
      const frequencyFields = {
        salary_frequency: data.salary_frequency || 'monthly',
        weekly_payment_type: data.salary_frequency === 'weekly' ? (data.weekly_payment_type || null) : null,
        hourly_rate: data.salary_frequency === 'weekly' && data.weekly_payment_type === 'hourly' ? (data.hourly_rate || null) : null,
        projected_hours_per_week: data.salary_frequency === 'weekly' && data.weekly_payment_type === 'hourly' ? (data.projected_hours_per_week || null) : null,
        payment_schedule_type: data.payment_schedule_type || 'fixed_day',
        payment_day_of_month: data.payment_schedule_type === 'fixed_day' ? (data.payment_day_of_month || null) : null,
        payment_day_of_week: data.payment_schedule_type === 'weekday' ? (data.payment_day_of_week || null) : null,
        payment_biweekly_days: paymentBiweeklyDays,
      };
      
      if (editingEmployee) {
        await updateEmployee.mutateAsync({
          id: editingEmployee.id,
          ...syncedData,
          business_unit: businessUnitToSave,
          account_code: data.account_code === 'auto' ? null : data.account_code || null,
          app_user_id: appUserIdToSave,
          ...frequencyFields,
        });
        toast.success('Funcionário atualizado com sucesso!');
      } else {
        await createEmployee.mutateAsync({
          full_name: syncedData.full_name,
          role: syncedData.role,
          base_salary: syncedData.base_salary,
          department: syncedData.department,
          business_unit: businessUnitToSave,
          salary_currency: syncedData.salary_currency,
          hire_date: syncedData.hire_date,
          email: syncedData.email,
          cost_type: syncedData.cost_type || 'fixed',
          account_code: data.account_code === 'auto' ? undefined : data.account_code || undefined,
          app_user_id: appUserIdToSave,
          ...frequencyFields,
        } as any);
        toast.success('Funcionário cadastrado com sucesso!');
      }
      handleCloseDialog();
    } catch (error: any) {
      console.error('Erro ao salvar funcionário:', error);
      toast.error(error?.message || 'Erro ao salvar funcionário. Verifique os dados e tente novamente.');
    }
  };

  const handleDelete = async () => {
    if (!deletingEmployee) return;
    
    try {
      await deleteEmployee.mutateAsync(deletingEmployee.id);
      toast.success('Funcionário desativado com sucesso!');
      setDeletingEmployee(null);
    } catch (error: any) {
      console.error('Erro ao excluir funcionário:', error);
      toast.error(error?.message || 'Erro ao excluir funcionário.');
    }
  };

  const isSubmitting = createEmployee.isPending || updateEmployee.isPending;

  return (
    <FinancePageLayout>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Funcionários</h1>

        <Dialog open={isDialogOpen} onOpenChange={(open) => open ? handleOpenDialog() : handleCloseDialog()}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Novo Funcionário
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingEmployee ? 'Editar Funcionário' : 'Adicionar Funcionário'}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="full_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome *</FormLabel>
                        <FormControl>
                          <Input placeholder="Nome completo" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cargo *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o cargo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {roles.map((role) => (
                              <SelectItem key={role.id} value={role.name}>
                                {role.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="department"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Departamento</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {departments.map((dept) => (
                              <SelectItem key={dept.id} value={dept.name}>
                                {dept.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="business_unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Unit</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="us">🇺🇸 USA</SelectItem>
                            <SelectItem value="br">🇧🇷 Brasil</SelectItem>
                            <SelectItem value="shared">🔗 Compartilhado</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>E-mail</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="email@exemplo.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="hire_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data de Início</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="cost_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Custo</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="fixed">Fixo (sem comissão)</SelectItem>
                          <SelectItem value="variable">Com Comissão</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Remuneração Section - Frequency BEFORE Value */}
                <div className="border-t pt-4 mt-2">
                  <h4 className="text-sm font-medium mb-3 text-muted-foreground">Remuneração</h4>
                  
                  {/* Frequency Selection - FIRST */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="salary_frequency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Frequência de Pagamento *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || 'monthly'}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="monthly">Mensal</SelectItem>
                              <SelectItem value="biweekly">Quinzenal</SelectItem>
                              <SelectItem value="weekly">Semanal</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {salaryFrequency === 'weekly' && (
                      <FormField
                        control={form.control}
                        name="weekly_payment_type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Tipo de Pagamento</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value || 'fixed'}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="fixed">Valor Fixo</SelectItem>
                                <SelectItem value="hourly">Por Hora</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>

                  {/* Value Fields - Dynamic based on frequency */}
                  <div className={`grid gap-4 mt-4 ${salaryFrequency === 'weekly' && weeklyPaymentType === 'hourly' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {salaryFrequency === 'weekly' && weeklyPaymentType === 'hourly' ? (
                      <>
                        <FormField
                          control={form.control}
                          name="hourly_rate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Valor/Hora *</FormLabel>
                              <FormControl>
                                <Input type="number" step="0.01" placeholder="25.00" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="projected_hours_per_week"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Horas/Semana *</FormLabel>
                              <FormControl>
                                <Input type="number" step="0.5" placeholder="40" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="salary_currency"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Moeda *</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Moeda" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="USD">USD</SelectItem>
                                  <SelectItem value="BRL">BRL</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </>
                    ) : (
                      <>
                        <FormField
                          control={form.control}
                          name="base_salary"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {salaryFrequency === 'weekly' ? 'Valor Semanal *' :
                                 salaryFrequency === 'biweekly' ? 'Valor por Quinzena *' :
                                 'Salário Mensal *'}
                              </FormLabel>
                              <FormControl>
                                <Input type="number" placeholder="5000" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="salary_currency"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Moeda *</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Moeda" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="USD">USD</SelectItem>
                                  <SelectItem value="BRL">BRL</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </>
                    )}
                  </div>

                  {/* Estimated Monthly Cost Indicator - Calendar-based */}
                  {(() => {
                    const baseSalary = form.watch('base_salary') || 0;
                    const currency = form.watch('salary_currency') || 'USD';
                    const hourlyRate = form.watch('hourly_rate') || 0;
                    const hoursPerWeek = form.watch('projected_hours_per_week') || 0;
                    const paymentDayOfWeek = form.watch('payment_day_of_week');
                    
                    if (salaryFrequency === 'monthly') {
                      return null; // No need to show for monthly
                    }
                    
                    if (salaryFrequency === 'biweekly' && baseSalary > 0) {
                      const monthlyEstimate = baseSalary * 2;
                      return (
                        <div className="mt-3 p-3 bg-muted/50 rounded-lg text-sm">
                          <span className="text-muted-foreground">💡 Custo mensal: </span>
                          <span className="font-medium">{formatCurrency(monthlyEstimate, currency)}</span>
                          <span className="text-muted-foreground"> (2x por mês)</span>
                        </div>
                      );
                    }
                    
                    if (salaryFrequency === 'weekly') {
                      // Calculate weekly value
                      let weeklyValue = 0;
                      if (weeklyPaymentType === 'hourly' && hourlyRate > 0 && hoursPerWeek > 0) {
                        weeklyValue = hourlyRate * hoursPerWeek;
                      } else if (baseSalary > 0) {
                        weeklyValue = baseSalary;
                      }
                      
                      if (weeklyValue <= 0) return null;
                      
                      // If payment day is configured, calculate exact months
                      if (paymentDayOfWeek !== undefined && paymentDayOfWeek !== null) {
                        const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
                        const now = new Date();
                        const currentYear = now.getFullYear();
                        const currentMonth = now.getMonth();
                        const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
                        const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;
                        
                        // Count payment days in a month
                        const countPaymentDays = (year: number, month: number, dayOfWeek: number) => {
                          const lastDay = new Date(year, month + 1, 0).getDate();
                          let count = 0;
                          for (let d = 1; d <= lastDay; d++) {
                            if (new Date(year, month, d).getDay() === dayOfWeek) count++;
                          }
                          return count;
                        };
                        
                        const currentMonthPayments = countPaymentDays(currentYear, currentMonth, paymentDayOfWeek);
                        const nextMonthPayments = countPaymentDays(nextMonthYear, nextMonth, paymentDayOfWeek);
                        const currentMonthCost = weeklyValue * currentMonthPayments;
                        const nextMonthCost = weeklyValue * nextMonthPayments;
                        
                        const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                                          'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
                        
                        return (
                          <div className="mt-3 p-3 bg-muted/50 rounded-lg text-sm space-y-1">
                            <div>
                              <span className="text-muted-foreground">📅 {monthNames[currentMonth]}/{currentYear}: </span>
                              <span className="font-medium">{formatCurrency(currentMonthCost, currency)}</span>
                              <span className="text-muted-foreground text-xs"> ({currentMonthPayments} {dayNames[paymentDayOfWeek].toLowerCase()}s)</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">📅 {monthNames[nextMonth]}/{nextMonthYear}: </span>
                              <span className="font-medium">{formatCurrency(nextMonthCost, currency)}</span>
                              <span className="text-muted-foreground text-xs"> ({nextMonthPayments} {dayNames[paymentDayOfWeek].toLowerCase()}s)</span>
                            </div>
                          </div>
                        );
                      }
                      
                      // Generic estimate if no payment day configured
                      const minMonthly = weeklyValue * 4;
                      const maxMonthly = weeklyValue * 5;
                      return (
                        <div className="mt-3 p-3 bg-muted/50 rounded-lg text-sm">
                          <span className="text-muted-foreground">💡 Custo estimado: </span>
                          <span className="font-medium">~{formatCurrency(minMonthly, currency)} a {formatCurrency(maxMonthly, currency)}/mês</span>
                          <span className="text-muted-foreground block text-xs mt-1">Configure o dia de pagamento abaixo para cálculo exato</span>
                        </div>
                      );
                    }
                    
                    return null;
                  })()}
                </div>

                {/* DRE Category & System User */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="account_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Categoria DRE</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || 'auto'}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a categoria no DRE" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="auto">Automático (baseado no cargo)</SelectItem>
                            {DRE_CATEGORIES.map((cat) => (
                              <SelectItem key={cat.code} value={cat.code}>
                                {cat.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="app_user_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">
                          <Link2 className="h-3 w-3" />
                          Usuário do Sistema
                        </FormLabel>
                        <Select 
                          onValueChange={(value) => {
                            field.onChange(value);
                            // Auto-fill name and email when selecting a user
                            if (value && value !== 'none') {
                              const selectedUser = getAppUserById(value);
                              if (selectedUser) {
                                form.setValue('full_name', getAppUserDisplayName(selectedUser));
                                if (selectedUser.email) {
                                  form.setValue('email', selectedUser.email);
                                }
                              }
                            }
                          }} 
                          value={field.value || 'none'}
                          disabled={loadingAppUsers}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Vincular a um usuário" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">Nenhum (sem acesso ao sistema)</SelectItem>
                            {availableAppUsers.map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {getAppUserDisplayName(user)} {user.email ? `(${user.email})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Payment Schedule Section */}
                <div className="border-t pt-4 mt-2">
                  <h4 className="text-sm font-medium mb-3 text-muted-foreground">Data de Pagamento</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="payment_schedule_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tipo de Data</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || 'fixed_day'}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="fixed_day">Dia Fixo do Mês</SelectItem>
                              <SelectItem value="weekday">Dia da Semana</SelectItem>
                              <SelectItem value="biweekly_days">Duas Datas no Mês</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {paymentScheduleType === 'fixed_day' && (
                      <FormField
                        control={form.control}
                        name="payment_day_of_month"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Dia do Mês (1-31)</FormLabel>
                            <FormControl>
                              <Input type="number" min={1} max={31} placeholder="15" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {paymentScheduleType === 'weekday' && (
                      <FormField
                        control={form.control}
                        name="payment_day_of_week"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Dia da Semana</FormLabel>
                            <Select onValueChange={(v) => field.onChange(Number(v))} value={field.value?.toString() || undefined}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="0">Domingo</SelectItem>
                                <SelectItem value="1">Segunda-feira</SelectItem>
                                <SelectItem value="2">Terça-feira</SelectItem>
                                <SelectItem value="3">Quarta-feira</SelectItem>
                                <SelectItem value="4">Quinta-feira</SelectItem>
                                <SelectItem value="5">Sexta-feira</SelectItem>
                                <SelectItem value="6">Sábado</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>

                  {paymentScheduleType === 'biweekly_days' && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <FormField
                        control={form.control}
                        name="payment_biweekly_day1"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>1º Dia do Mês</FormLabel>
                            <FormControl>
                              <Input type="number" min={1} max={31} placeholder="15" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="payment_biweekly_day2"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>2º Dia do Mês</FormLabel>
                            <FormControl>
                              <Input type="number" min={1} max={31} placeholder="30" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={handleCloseDialog}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {editingEmployee ? 'Atualizar' : 'Salvar'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card className="border-purple-200 bg-purple-50/50 dark:bg-purple-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Folha Consolidada</CardTitle>
            <Globe className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(totalConsolidatedUSD, 'USD')}
            </div>
            <p className="text-xs text-muted-foreground">
              USD + BRL convertido
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Funcionários</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredSummary.count}</div>
            <p className="text-xs text-muted-foreground">
              ativos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Folha Mensal (USD)</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(filteredSummary.totalUSD, 'USD')}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Folha Mensal (BRL)</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(filteredSummary.totalBRL, 'BRL')}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aniversários este Mês</CardTitle>
            <Gift className="h-4 w-4 text-pink-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-pink-600">
              {birthdays?.length || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar funcionário..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={businessUnitFilter} onValueChange={(v) => setBusinessUnitFilter(v as BusinessUnitFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Business Unit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">🌐 Todos</SelectItem>
            <SelectItem value="us">🇺🇸 USA</SelectItem>
            <SelectItem value="br">🇧🇷 Brasil</SelectItem>
            <SelectItem value="shared">🔗 Compartilhado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Categoria DRE</TableHead>
                  <TableHead>BU</TableHead>
                  <TableHead className="text-right">Salário</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                      Nenhum funcionário encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  activeEmployees.map((employee) => {
                    const dreCategory = getDreCategoryLabel(employee.account_code);
                    const linkedUser = getAppUserById(employee.app_user_id);
                    return (
                    <TableRow key={employee.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{employee.full_name}</span>
                          {linkedUser && (
                            <Badge 
                              variant="outline" 
                              className="border-blue-200 text-blue-600 bg-blue-50 cursor-pointer hover:bg-blue-100 text-xs"
                              onClick={() => navigate(createPageUrl('UserDetail') + `?id=${employee.app_user_id}`)}
                            >
                              <Link2 className="h-3 w-3 mr-1" />
                              Vinculado
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{employee.role || '-'}</TableCell>
                      <TableCell>
                        {dreCategory ? (
                          <Badge 
                            variant="outline"
                            className={
                              dreCategory.group === 'CSP' 
                                ? 'border-green-500 text-green-600 bg-green-50' 
                                : dreCategory.group === 'CAC'
                                ? 'border-blue-500 text-blue-600 bg-blue-50'
                                : 'border-orange-500 text-orange-600 bg-orange-50'
                            }
                          >
                            {dreCategory.group}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">Auto</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {employee.business_unit === 'us' ? '🇺🇸 US' : employee.business_unit === 'br' ? '🇧🇷 BR' : '🌐 Shared'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(employee.base_salary, employee.salary_currency)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={employee.status === 'active' ? 'default' : 'secondary'}>
                          {employee.status === 'active' ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleOpenDialog(employee)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setDeletingEmployee(employee)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );})
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingEmployee} onOpenChange={() => setDeletingEmployee(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar Funcionário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja desativar {deletingEmployee?.full_name}? 
              Esta ação irá marcar o funcionário como inativo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteEmployee.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Desativar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FinancePageLayout>
  );
}
